import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { UserProfile } from "@/components/feature/Sidebar";
import TeacherRequestCard from "../components/TeacherRequestCard";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";
import { inputService } from "@/services/api";
import { mapDbInsumoToFrontend } from "@/services/mapping";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

type FilterStatus = "todas" | "pendiente" | "en_desarrollo" | "entregado" | "devuelto_correccion" | "aprobado" | "vencido";

const FILTERS: { key: FilterStatus; label: string }[] = [
  { key: "todas", label: "Todas" },
  { key: "pendiente", label: "Pendientes" },
  { key: "en_desarrollo", label: "En desarrollo" },
  { key: "entregado", label: "Enviadas" },
  { key: "devuelto_correccion", label: "Devueltas" },
  { key: "aprobado", label: "Aprobadas" },
  { key: "vencido", label: "Vencidas" },
];

export default function DocenteSolicitudesPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [filter, setFilter] = useState<FilterStatus>("todas");
  const [search, setSearch] = useState("");
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const loadData = async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError("");
      // Get all solicitados and filter by docente_id
      const data = await inputService.listSolicitados();
      if (Array.isArray(data)) {
        const mapped = data
          .map(mapDbInsumoToFrontend)
          .filter((t) => String(t.docenteId) === String(user.id) || t.docenteName === user.nombre);
        setRequests(mapped);
      }
    } catch (err: any) {
      console.error("Error loading pending teacher inputs:", err);
      setError("Error al cargar solicitudes: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const filtered = useMemo(() => {
    let result = requests;
    if (filter !== "todas") {
      result = result.filter((t) => t.estado === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((t) =>
        t.tipoInsumo.toLowerCase().includes(q) ||
        t.descripcionSolicitud.toLowerCase().includes(q) ||
        t.studentName.toLowerCase().includes(q)
      );
    }
    return result;
  }, [requests, filter, search]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-foreground-300 text-2xl animate-spin" />
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <ScrollReveal className="scroll-reveal">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Solicitudes de Insumos</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Reportes, antecedentes y bitácoras solicitadas por el equipo de apoyo.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/docente/historial")}
              className="btn-modern-ghost px-3.5 py-2 text-xs font-semibold"
            >
              Ver Historial
            </button>
            <button
              onClick={() => navigate("/docente/devoluciones")}
              className="btn-modern-ghost px-3.5 py-2 text-xs font-semibold text-red-600 hover:text-red-700"
            >
              Devoluciones
            </button>
          </div>
        </div>
      </ScrollReveal>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {/* Filters & Search */}
      <ScrollReveal className="scroll-reveal">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="text"
              placeholder="Buscar por tipo de informe o estudiante..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-background-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent placeholder-foreground-400"
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all whitespace-nowrap cursor-pointer ${
                  filter === f.key
                    ? "bg-foreground-900 text-white border-foreground-900"
                    : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </ScrollReveal>

      {/* Request Cards */}
      {loading ? (
        <div className="text-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mr-2" /> Cargando solicitudes...
        </div>
      ) : filtered.length === 0 ? (
        <ScrollReveal className="scroll-reveal">
          <div className="text-center py-16 bg-white border border-background-200/50 rounded-2xl">
            <div className="w-12 h-12 rounded-xl bg-background-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-line text-xl text-foreground-400" />
            </div>
            <p className="text-sm font-semibold text-foreground-900">No hay solicitudes</p>
            <p className="text-xs text-foreground-500 mt-0.5">
              {filter !== "todas" ? "Prueba cambiando el filtro de búsqueda" : "Te avisaremos cuando haya requerimientos"}
            </p>
          </div>
        </ScrollReveal>
      ) : (
        <ScrollRevealContainer>
          <div className="space-y-3">
            {filtered.map((req) => (
              <ScrollReveal key={req.id}>
                <TeacherRequestCard
                  request={req}
                  onClick={(id) => navigate(`/docente/solicitudes/${id}`)}
                />
              </ScrollReveal>
            ))}
          </div>
        </ScrollRevealContainer>
      )}
    </div>
  );
}